import React, { useEffect } from 'react';
import { useTranslate } from '../../mixins/translate/translate';
import { assembleUrl } from '../../store/assembleUrl';
import { fetchJson } from '../../store/fetch';
import { Modal } from '../common/Modal/Modal';
import { EditPanelButton } from '../elements';
import { Bounds, GenericMap } from '../utils/GenericMap';

interface IObject {
    id: string;
    name: string;
    code: string;
    location: string;
    latitude: number;
    longitude: number;
    surveyCondition?: string;
    surveyInspectionStandardType?: string;
    shapeGeoJson?: string;
    objectTypeName: string;
}

export interface IObjectDrawShapeModalProps {
    center: { lat: number; lng: number };
    isPdokMapLayerAvailable: boolean;
    onClose: () => void;
    onSave: (shapeGeoJson: Record<string, unknown> | undefined) => void;
    initialShapeGeoJson?: undefined | Record<string, unknown>;
    zoom: number;
}

function objectToMarker(object: IObject) {
    return {
        key: object.id,
        position: { lat: object.latitude, lng: object.longitude },
        title: object.name,
        color: '#8e7cc3',
        shapeGeoJson: object.shapeGeoJson ? JSON.parse(object.shapeGeoJson) : undefined,
        condition: object.surveyCondition,
        isTerrain: object.objectTypeName.toLowerCase() === 'terrein' || object.objectTypeName.toLowerCase() === 'terrain',
    };
}

export function ObjectDrawShapeModal(props: IObjectDrawShapeModalProps) {
    const translate = useTranslate();

    const [shapeGeoJson, setShapeGeoJson] = React.useState<Record<string, unknown> | undefined>(props.initialShapeGeoJson);

    const [objectMarkers, setObjectMarkers] = React.useState<IObject[]>([]);
    const [mapBounds, setMapBounds] = React.useState<Bounds | undefined>();
    const [mapZoom, setMapZoom] = React.useState(props.zoom);
    const [isShowMarkers, setShowMarkers] = React.useState(false);

    useEffect(() => {
        async function loadMarkers() {
            if (!mapBounds || mapZoom < 14) {
                setObjectMarkers([]);
                return;
            }
            const url = assembleUrl('/api/objectmarkers', {
                boundingBoxXMin: mapBounds.xMin,
                boundingBoxXMax: mapBounds.xMax,
                boundingBoxYMin: mapBounds.yMin,
                boundingBoxYMax: mapBounds.yMax,
            });
            const result = await fetchJson(url, 'get');
            if (result.status === 200) {
                setObjectMarkers(result.json.items);
            }
        }

        void loadMarkers();
    }, [mapBounds, mapZoom]);

    const markers = isShowMarkers ? objectMarkers.map(objectToMarker) : [];

    return (
        <Modal id="map-dialog" classNames="confirm-dialog" closeOnBackdropClick={true} onClose={props.onClose}>
            <Modal.Content isScrollBarVisible={true} style={{ height: '650px', width: '800px' }}>
                <GenericMap
                    onShapeGeoJsonChange={(newShapeGeoJson) => setShapeGeoJson(newShapeGeoJson)}
                    zoom={props.zoom}
                    isDroneNoFlyMapNlAvailable={false}
                    isPdokMapLayerAvailable={props.isPdokMapLayerAvailable}
                    center={props.center}
                    shapeGeoJson={shapeGeoJson}
                    markers={markers}
                    isDrawable
                    isShowObjects={isShowMarkers}
                    onBoundsChanged={(bounds) => setMapBounds(bounds)}
                    onZoomChanged={(zoom) => setMapZoom(zoom)}
                    onShowObjects={() => setShowMarkers(!isShowMarkers)}
                />
            </Modal.Content>
            <Modal.Status></Modal.Status>
            <Modal.Actions>
                <EditPanelButton small={true} text={translate('common.cancel')} onClick={props.onClose} />
                <EditPanelButton small={true} text={translate('common.save')} primary onClick={() => props.onSave(shapeGeoJson)} />
            </Modal.Actions>
        </Modal>
    );
}
